import type { TError, TPostSort, TPostType, TPostWithAuthor } from '@onesource/schemas';
import { useInfiniteQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

/**
 * API response shape for posts endpoint
 */
interface PostsResponse {
  posts: TPostWithAuthor[];
  total: number;
}

/**
 * Pagination params for infinite query
 */
interface PostsPageParams {
  limit: number;
  offset: number;
}

/**
 * Search / filter / sort params for the feed. All optional — omitted values
 * fall back to the API defaults (no filters, recent_activity sort).
 */
export type TFeedFilters = {
  search?: string;
  postTypes?: TPostType[];
  dateFrom?: string;
  dateTo?: string;
  authorId?: string;
  /** Available-tag values (question tag id or subspace/lesson source id) — resolved server-side */
  tagIds?: string[];
  /** Only posts the current user has saved (bookmarked) */
  saved?: boolean;
  /** Only the current user's own posts */
  mine?: boolean;
  sort?: TPostSort;
};

const DEFAULT_PAGE_SIZE = 10;

const hasActiveFilters = (filters?: TFeedFilters): boolean =>
  !!filters && Object.values(filters).some(v => (Array.isArray(v) ? v.length > 0 : v !== undefined));

/**
 * Query key for fetching posts with infinite scroll.
 * The unfiltered key stays a prefix of every filtered key, so invalidating
 * `[ENTITY, 'posts', 'infinite', spaceId]` covers all filtered variants too.
 */
export const getPostsInfiniteQueryKey = (spaceId: string, filters?: TFeedFilters) =>
  hasActiveFilters(filters) ? [ENTITY, 'posts', 'infinite', spaceId, filters] : [ENTITY, 'posts', 'infinite', spaceId];

/**
 * Query key for fetching posts with infinite scroll - hook version
 */
export const useGetPostsInfiniteQueryKey = (spaceId: string, filters?: TFeedFilters) =>
  getPostsInfiniteQueryKey(spaceId, filters);

/**
 * Hook to fetch posts with infinite scrolling
 * Returns posts ordered by the requested sort (default: most recent activity),
 * pinned posts always first.
 *
 * @param spaceId - Space ID (UUID) - should be a feed sub-space
 * @param pageSize - Number of posts per page (default: 10)
 * @param filters - Optional search / filter / sort params
 */
export const useGetPostsInfinite = (spaceId: string, pageSize: number = DEFAULT_PAGE_SIZE, filters?: TFeedFilters) => {
  return useInfiniteQuery<PostsResponse, TError>({
    queryKey: useGetPostsInfiniteQueryKey(spaceId, filters),
    queryFn: async ({ pageParam }) => {
      const params = pageParam as PostsPageParams;
      const res = await axios.get<PostsResponse>(`/community-hub/spaces/${spaceId}/posts`, {
        params: {
          limit: params.limit,
          offset: params.offset,
          search: filters?.search || undefined,
          postTypes: filters?.postTypes?.length ? filters.postTypes.join(',') : undefined,
          dateFrom: filters?.dateFrom,
          dateTo: filters?.dateTo,
          authorId: filters?.authorId,
          tags: filters?.tagIds?.length ? filters.tagIds.join(',') : undefined,
          saved: filters?.saved || undefined,
          mine: filters?.mine || undefined,
          sort: filters?.sort,
        },
      });
      return res.data;
    },
    initialPageParam: { limit: pageSize, offset: 0 } as PostsPageParams,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((acc, page) => acc + page.posts.length, 0);
      if (loadedCount >= lastPage.total) {
        return undefined; // No more pages
      }
      return { limit: pageSize, offset: loadedCount } as PostsPageParams;
    },
    enabled: !!spaceId,
    staleTime: 60 * 1000, // 1 minute (posts change frequently)
  });
};
